import { usePresenceStore } from './usePresenceStore';
import { create } from 'zustand';
import * as api from '../lib/api';
import type { ChannelMember } from '../lib/api';
import type { Channel } from '../types';
import { useAuthStore } from './useAuthStore';
import { captureChannelContext, useChannelStore } from './useChannelStore';
import { useDmStore } from './useDmStore';

export type ChannelRosterUnavailableReason = 'unknown-conversation' | 'not-member';
export type ChannelRosterResult =
  | { status: 'ready'; members: ChannelMember[] }
  | { status: 'unavailable'; reason: ChannelRosterUnavailableReason };

export type ChannelRosterAdmission =
  | { available: true }
  | { available: false; reason: ChannelRosterUnavailableReason };

interface ChannelMemberState {
  byChannelId: Record<string, ChannelMember[]>;
  loadedVersionByChannelId: Record<string, number>;
  loadingByChannelId: Record<string, boolean>;
  errorsByChannelId: Record<string, Error>;
  threadChannelsById: Record<string, Channel>;
  threadRosterProofById: Record<string, boolean>;
  loadChannelMembers: (channelId: string, version?: number) => Promise<ChannelRosterResult>;
  prepareThreadRoster: (threadId: string) => (channel: Channel, members: ChannelMember[]) => 'applied' | 'stale' | 'discarded';
  updateChannelMember: (channelId: string, userId: string, updates: Partial<ChannelMember>) => void;
  clearChannel: (channelId: string) => void;
  reset: () => void;
}

interface MemberRequest {
  channelId: string;
  version: number;
  promise: Promise<ChannelRosterResult>;
}

const outputEpochByChannel = new Map<string, number>();
const inFlight = new Map<string, MemberRequest>();
const channelEpochs = new Map<string, number>();
const threadProofVersions = new Map<string, number>();
let membersGeneration = 0;

const unavailable = (reason: ChannelRosterUnavailableReason): ChannelRosterResult => ({ status: 'unavailable', reason });

/** Roster authority is independent of public conversation history access. */
export const channelRosterAdmission = (channelId: string | null | undefined): ChannelRosterAdmission => {
  if (!channelId || !useAuthStore.getState().currentUser) return { available: false, reason: 'unknown-conversation' };
  const channel = useChannelStore.getState().channels.find((row) => row.id === channelId);
  if (channel?.channel_type === 0) {
    return channel.is_member === true ? { available: true } : { available: false, reason: 'not-member' };
  }
  if (useDmStore.getState().dmChannels.some((row) => row.id === channelId)) return { available: true };
  const { threadChannelsById, threadRosterProofById } = useChannelMemberStore.getState();
  const thread = threadChannelsById[channelId];
  if (!thread) return { available: false, reason: 'unknown-conversation' };
  const parent = useChannelStore.getState().channels.find((row) => row.id === thread.parent_id);
  if (parent?.channel_type === 0 && parent.is_member !== true) return { available: false, reason: 'not-member' };
  return threadRosterProofById[channelId] === true &&
    threadProofVersions.get(channelId) === (useChannelStore.getState().channelMembersVersion[channelId] ?? 0)
    ? { available: true }
    : { available: false, reason: 'not-member' };
};

const readFence = (channelId: string) => {
  const generation = membersGeneration;
  const epoch = channelEpochs.get(channelId) ?? 0;
  const sameContext = captureChannelContext();
  return () => generation === membersGeneration && epoch === (channelEpochs.get(channelId) ?? 0) && sameContext();
};

const revokeRoster = (channelId: string) => {
  channelEpochs.set(channelId, (channelEpochs.get(channelId) ?? 0) + 1);
  for (const [key, request] of inFlight) if (request.channelId === channelId) inFlight.delete(key);
  useChannelMemberStore.setState((state) => {
    const byChannelId = { ...state.byChannelId };
    const loadedVersionByChannelId = { ...state.loadedVersionByChannelId };
    const loadingByChannelId = { ...state.loadingByChannelId };
    const errorsByChannelId = { ...state.errorsByChannelId };
    delete byChannelId[channelId];
    delete loadedVersionByChannelId[channelId];
    delete loadingByChannelId[channelId];
    delete errorsByChannelId[channelId];
    return { byChannelId, loadedVersionByChannelId, loadingByChannelId, errorsByChannelId };
  });
};

export const useChannelMemberStore = create<ChannelMemberState>()((set, get) => ({
  byChannelId: {},
  loadedVersionByChannelId: {},
  loadingByChannelId: {},
  errorsByChannelId: {},
  threadChannelsById: {},
  threadRosterProofById: {},

  loadChannelMembers: (channelId, version = 0) => {
    const admission = channelRosterAdmission(channelId);
    if (!admission.available) {
      const state = get();
      if (channelId in state.byChannelId || channelId in state.loadingByChannelId || channelId in state.errorsByChannelId) revokeRoster(channelId);
      return Promise.resolve(unavailable(admission.reason));
    }
    const outputEpoch = usePresenceStore.getState().providerOutputEpoch;
    const state = get();
    const cached = state.byChannelId[channelId];
    const loadedVersion = state.loadedVersionByChannelId[channelId];
    if (cached && loadedVersion !== undefined && loadedVersion >= version && outputEpochByChannel.get(channelId) === outputEpoch) return Promise.resolve({ status: 'ready', members: cached });
    const key = `${channelId}:${version}:${outputEpoch}`;
    const pending = inFlight.get(key);
    if (pending) return pending.promise;
    const isCurrent = readFence(channelId);
    set((current) => {
      const errorsByChannelId = { ...current.errorsByChannelId };
      delete errorsByChannelId[channelId];
      return { errorsByChannelId, loadingByChannelId: { ...current.loadingByChannelId, [channelId]: true } };
    });
    const applyOutputSnapshot = usePresenceStore.getState().prepareProviderOutputSnapshot();
    const request = api.fetchChannelMembers(channelId).then((members): ChannelRosterResult => {
      if (!isCurrent()) return unavailable('unknown-conversation');
      const currentAdmission = channelRosterAdmission(channelId);
      if (!currentAdmission.available) return unavailable(currentAdmission.reason);
      const latest = get();
      if ((latest.loadedVersionByChannelId[channelId] ?? -1) > version) return { status: 'ready', members: latest.byChannelId[channelId]! };
      set((current) => {
        if ((current.loadedVersionByChannelId[channelId] ?? -1) > version) return current;
        return {
          byChannelId: { ...current.byChannelId, [channelId]: members },
          loadedVersionByChannelId: { ...current.loadedVersionByChannelId, [channelId]: version },
        };
      });
      outputEpochByChannel.set(channelId, outputEpoch);
      applyOutputSnapshot(members);
      return { status: 'ready', members };
    }).catch((error: unknown): ChannelRosterResult => {
      if (!isCurrent()) return unavailable('unknown-conversation');
      const currentAdmission = channelRosterAdmission(channelId);
      if (!currentAdmission.available) return unavailable(currentAdmission.reason);
      const retained = error instanceof Error ? error : new Error('Could not load channel members.', { cause: error });
      if ((get().loadedVersionByChannelId[channelId] ?? -1) <= version) {
        set((current) => ({ errorsByChannelId: { ...current.errorsByChannelId, [channelId]: retained } }));
      }
      throw error;
    }).finally(() => {
      if (inFlight.get(key)?.promise !== request) return;
      inFlight.delete(key);
      if (isCurrent()) set((current) => ({ loadingByChannelId: { ...current.loadingByChannelId, [channelId]: [...inFlight.values()].some((entry) => entry.channelId === channelId && entry.version >= (current.loadedVersionByChannelId[channelId] ?? -1)) } }));
    });
    inFlight.set(key, { channelId, version, promise: request });
    return request;
  },

  prepareThreadRoster: (threadId) => {
    const outputEpoch = usePresenceStore.getState().providerOutputEpoch;
    const applyOutputSnapshot = usePresenceStore.getState().prepareProviderOutputSnapshot();
    const isCurrent = readFence(threadId);
    const actorId = useAuthStore.getState().currentUser?.id;
    const version = useChannelStore.getState().channelMembersVersion[threadId] ?? 0;
    const membership = new Map(useChannelStore.getState().channels.map((channel) => [channel.id, channel.is_member]));
    return (thread, members) => {
      if (!isCurrent() || !actorId) return 'discarded';
      if (thread.id !== threadId || thread.channel_type !== 11 || !thread.parent_id) throw new Error('Invalid thread roster context.');
      const parent = useChannelStore.getState().channels.find((channel) => channel.id === thread.parent_id);
      set((state) => ({ threadChannelsById: { ...state.threadChannelsById, [threadId]: thread } }));
      if (membership.get(thread.parent_id) !== parent?.is_member ||
        version !== (useChannelStore.getState().channelMembersVersion[threadId] ?? 0)) return 'stale';
      // The exact response has already applied both root and leaf member-surface authorization.
      const proof = thread.is_member === true && members.some((member) => member.user_id === actorId) &&
        !(parent?.channel_type === 0 && parent.is_member !== true);
      threadProofVersions.set(threadId, version);
      set((state) => ({ threadRosterProofById: { ...state.threadRosterProofById, [threadId]: proof } }));
      if (!channelRosterAdmission(threadId).available) {
        revokeRoster(threadId);
        return 'applied';
      }
      set((state) => ({
        byChannelId: { ...state.byChannelId, [threadId]: members },
        loadedVersionByChannelId: { ...state.loadedVersionByChannelId, [threadId]: version },
      }));
      outputEpochByChannel.set(threadId, outputEpoch);
      applyOutputSnapshot(members);
      return 'applied';
    };
  },

  updateChannelMember: (channelId, userId, updates) => {
    if (!channelRosterAdmission(channelId).available) return;
    set((state) => {
      const members = state.byChannelId[channelId];
      if (!members?.some((member) => member.user_id === userId)) return state;
      return { byChannelId: { ...state.byChannelId, [channelId]: members.map((member) =>
        member.user_id === userId ? { ...member, ...updates } : member) } };
    });
  },

  clearChannel: (channelId) => {
    revokeRoster(channelId);
    threadProofVersions.delete(channelId);
    set((state) => {
      const threadChannelsById = { ...state.threadChannelsById };
      const threadRosterProofById = { ...state.threadRosterProofById };
      delete threadChannelsById[channelId];
      delete threadRosterProofById[channelId];
      return { threadChannelsById, threadRosterProofById };
    });
  },

  reset: () => {
    membersGeneration++;
    inFlight.clear();
    outputEpochByChannel.clear();
    channelEpochs.clear();
    threadProofVersions.clear();
    set({ byChannelId: {}, loadedVersionByChannelId: {}, loadingByChannelId: {}, errorsByChannelId: {}, threadChannelsById: {}, threadRosterProofById: {} });
  },
}));

const revokeUnavailableRosters = () => {
  const state = useChannelMemberStore.getState();
  const tracked = new Set([...Object.keys(state.byChannelId), ...Object.keys(state.loadingByChannelId), ...Object.keys(state.errorsByChannelId)]);
  for (const channelId of tracked) if (!channelRosterAdmission(channelId).available) revokeRoster(channelId);
};

useChannelStore.subscribe((state, previous) => {
  if (state.channels === previous.channels && state.channelMembersVersion === previous.channelMembersVersion) return;
  const roster = useChannelMemberStore.getState();
  for (const [threadId, thread] of Object.entries(roster.threadChannelsById)) {
    const previousRoot = previous.channels.find((channel) => channel.id === thread.parent_id);
    const currentRoot = state.channels.find((channel) => channel.id === thread.parent_id);
    if (roster.threadRosterProofById[threadId] === true &&
      ((previousRoot && !currentRoot) || (currentRoot?.channel_type === 0 && currentRoot.is_member !== true))) {
      useChannelMemberStore.setState((current) => ({ threadRosterProofById: { ...current.threadRosterProofById, [threadId]: false } }));
    }
  }
  revokeUnavailableRosters();
});
useDmStore.subscribe((state, previous) => {
  if (state.dmChannels !== previous.dmChannels) revokeUnavailableRosters();
});
useAuthStore.subscribe((state, previous) => {
  if (state.currentUser?.id !== previous.currentUser?.id || state.guildId !== previous.guildId) useChannelMemberStore.getState().reset();
});
